import {
  Box,
  IconButton,
  Button,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import Divider from "@mui/material/Divider";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AddIcon from "@mui/icons-material/Add";
import "react-toastify/dist/ReactToastify.css";

const PublicHolidays = () => {

  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [holidayToDelete, setHolidayToDelete] = useState(null);

  const { getData, addData, updateData, deleteData } = useAuth();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [formData, setFormData] = useState({
    holiday_name: "",
    holiday_date: "",
    message: ""
  });

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    try {
      const response = await getData("proccess.php", "public_holidays");

      if (response.success) {
        setHolidays(response.data);
      }
    } catch (err) {
      toast.error("Failed to load holidays");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedHoliday(null);

    setFormData({
      holiday_name: "",
      holiday_date: "",
      message: ""
    });

    setOpenDialog(true);
  };

  const handleEdit = (row) => {
    setSelectedHoliday(row);

    setFormData({
      holiday_name: row.holiday_name,
      holiday_date: row.holiday_date,
      message: row.message
    });

    setOpenDialog(true);
  };

  const handleDelete = (row) => {
    setHolidayToDelete(row);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    const response = await deleteData(
      "proccess.php",
      "public_holidays",
      holidayToDelete.id
    );

    if (response.success) {
      toast.success("Holiday deleted");
      fetchHolidays();
    }

    setOpenDeleteDialog(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {

    let response;

    if (selectedHoliday) {
      response = await updateData(
        "proccess.php",
        "public_holidays",
        formData,
        selectedHoliday.id
      );
    } else {
      response = await addData(
        "proccess.php",
        "public_holidays",
        formData
      );
    }

    if (response.success) {
      toast.success("Saved successfully");
      fetchHolidays();
    } else {
      toast.error(response.message);
    }

    setOpenDialog(false);
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },

    {
      field: "holiday_name",
      headerName: "Holiday",
      flex: 1
    },

    {
      field: "holiday_date",
      headerName: "Date (MM-DD)",
      flex: 1
    },

    {
      field: "message",
      headerName: "Message",
      flex: 2
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Box display="flex">
          <IconButton
            onClick={() => handleEdit(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>

          <IconButton
            onClick={() => handleDelete(params.row)}
            sx={{ color: colors.red[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box m="20px">

      <ToastContainer />

      <Box
        mb="20px"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h4" fontWeight="bold">
          Public Holidays
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Holiday
        </Button>
      </Box>

      <Divider />

      <Box
        height="calc(100vh - 250px)"
        mt="20px"
        backgroundColor={colors.primary[400]}
      >
        <DataGrid
          rows={holidays}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          slots={{
            toolbar: GridToolbarQuickFilter
          }}
        />
      </Box>

      {/* Add/Edit Dialog */}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>

        <DialogTitle>
          {selectedHoliday ? "Edit Holiday" : "Add Holiday"}
        </DialogTitle>

        <DialogContent>

          <TextField
            margin="dense"
            label="Holiday Name"
            name="holiday_name"
            fullWidth
            value={formData.holiday_name}
            onChange={handleChange}
          />

          <TextField
            margin="dense"
            label="Date (MM-DD)"
            name="holiday_date"
            fullWidth
            placeholder="03-21"
            value={formData.holiday_date}
            onChange={handleChange}
          />

          <TextField
            margin="dense"
            label="Message"
            name="message"
            fullWidth
            multiline
            rows={3}
            value={formData.message}
            onChange={handleChange}
          />

        </DialogContent>

        <DialogActions>

          <Button onClick={() => setOpenDialog(false)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            endIcon={<SendIcon />}
            onClick={handleSubmit}
          >
            Save
          </Button>

        </DialogActions>

      </Dialog>

      {/* Delete Dialog */}

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Holiday</DialogTitle>

        <DialogContent>
          Are you sure you want to delete{" "}
          <b>{holidayToDelete?.holiday_name}</b>?
        </DialogContent>

        <DialogActions>

          <Button onClick={() => setOpenDeleteDialog(false)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>

        </DialogActions>

      </Dialog>

    </Box>
  );
};

export default PublicHolidays;